/**
 * One recorded Bear session, rally by rally (spec sections 24, 25, 35).
 *
 * The thing this screen exists to make visible is `contextQuality`. Each rally
 * member row is bound to the nearest hero/gear snapshot we had, and that
 * snapshot can sit hours away from the rally itself. When it does, the row
 * cannot support any "this upgrade produced that damage" reading — the loadout
 * we think they used may simply not be the loadout they used.
 *
 * So context quality is a column, not a tooltip, and a session containing STALE
 * or MISSING bindings says so at the top before any number is read.
 *
 * `contextAgeSeconds` is signed: negative means the snapshot was taken before
 * the rally, positive after. Both are shown, because a snapshot captured hours
 * *after* a rally describes the loadout no better than one captured hours
 * before it.
 *
 * Troop counts render as "not recorded" / `미입력` when absent, never as 0 —
 * the same rule the entry form enforces on the way in (spec section 56). The
 * Korean word is deliberately not a digit and not a blank, so an unentered cell
 * and a recorded zero cannot be confused at a glance.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { BearSessionDetailResponse } from '@kso/schemas';
import { useBearSession } from '../../api/queries';
import { Badge, Card, Notice, Value, WarningList } from '../../components/ui/primitives';
import { EmptyState, QueryBoundary } from '../../components/ui/states';
import { formatCompact, formatDateTime, formatDuration, formatNumber } from '../../lib/format';
import { useT } from '../../i18n';
import { EntryProgress, EntryStatusBadge, partialTotalsCaveat } from './SessionEntryProgress';

type Rally = BearSessionDetailResponse['rallies'][number];
type RallyMember = Rally['members'][number];
type ContextQuality = NonNullable<RallyMember['contextQuality']>;

const CONTEXT_TONE: Record<ContextQuality, string> = {
  EXACT: 'CONFIRMED',
  NEAR: 'RECENT',
  STALE: 'LOW',
  MISSING: 'UNKNOWN',
};

/** Bindings too far away to support an attribution claim. */
function isWeakContext(quality: RallyMember['contextQuality']): boolean {
  return quality === 'STALE' || quality === 'MISSING' || quality === null;
}

export function SessionDetail({ sessionId, onClose }: { sessionId: string; onClose: () => void }) {
  const detail = useBearSession(sessionId);
  const t = useT();

  return (
    <QueryBoundary query={detail} loadingLabel={t.sessionDetail.loading}>
      {(data) => <SessionDetailBody data={data} onClose={onClose} />}
    </QueryBoundary>
  );
}

function SessionDetailBody({
  data,
  onClose,
}: {
  data: BearSessionDetailResponse;
  onClose: () => void;
}) {
  const t = useT();
  const { session, rallies } = data;
  const caveat = partialTotalsCaveat(session);

  const allMembers = rallies.flatMap((rally) => rally.members);
  const weakContext = allMembers.filter((member) => isWeakContext(member.contextQuality)).length;
  const missingTroops = allMembers.filter((member) => member.totalTroops === null).length;

  return (
    <div className="stack">
      <Card
        title={
          <span className="row">
            {session.trapNumber === null
              ? t.sessionDetail.genericTitle
              : t.bearHistory.trapLabel(session.trapNumber)}
            <EntryStatusBadge status={session.entryStatus} />
          </span>
        }
        actions={
          <button type="button" className="btn btn--sm btn--subtle" onClick={onClose}>
            {t.common.close}
          </button>
        }
      >
        {caveat ? (
          <div style={{ marginBottom: 10 }}>
            <Notice severity={session.entryStatus === 'DRAFT' ? 'WARN' : 'INFO'}>{caveat}</Notice>
          </div>
        ) : null}

        <dl className="kv">
          <dt>{t.sessionDetail.started}</dt>
          <dd>
            <Value absent={t.common.notRecorded}>{formatDateTime(session.startedAt)}</Value>
          </dd>
          <dt>{t.sessionDetail.ended}</dt>
          <dd>
            <Value absent={t.common.notRecorded}>{formatDateTime(session.endedAt)}</Value>
          </dd>
          <dt>{t.sessionDetail.allianceTotalDamage}</dt>
          <dd>
            {session.allianceTotalDamage === null ? (
              <span className="absent">{t.common.notRecorded}</span>
            ) : (
              <span className="row" style={{ gap: 6 }}>
                {formatCompact(session.allianceTotalDamage)}
                <span className="faint small">{t.sessionDetail.asReportedByGame}</span>
              </span>
            )}
          </dd>
          <dt>{t.sessionDetail.entryCoverage}</dt>
          <dd>
            <EntryProgress session={session} />
          </dd>
          <dt>{t.sessionDetail.ralliesRecorded}</dt>
          <dd>{formatNumber(session.rallyCount)}</dd>
          {session.gameNotes ? (
            <>
              <dt>{t.sessionDetail.notes}</dt>
              <dd>{session.gameNotes}</dd>
            </>
          ) : null}
        </dl>

        <div style={{ marginTop: 10 }}>
          <WarningList warnings={data.warnings} />
        </div>

        {weakContext > 0 ? (
          <div style={{ marginTop: 8 }}>
            <Notice severity="WARN">
              {t.sessionDetail.weakContext(weakContext, allMembers.length)}
            </Notice>
          </div>
        ) : null}

        {missingTroops > 0 ? (
          <div style={{ marginTop: 8 }}>
            <Notice severity="INFO">
              {t.sessionDetail.missingTroops(missingTroops, allMembers.length)}
            </Notice>
          </div>
        ) : null}
      </Card>

      {rallies.length === 0 ? (
        <EmptyState
          title={t.sessionDetail.noRalliesTitle}
          body={t.sessionDetail.noRalliesBody}
        />
      ) : (
        [...rallies]
          .sort((a, b) => (a.wave ?? 0) - (b.wave ?? 0) || (a.sequence ?? 0) - (b.sequence ?? 0))
          .map((rally) => <RallyCard key={rally.id} rally={rally} />)
      )}
    </div>
  );
}

function RallyCard({ rally }: { rally: Rally }) {
  const t = useT();
  const [open, setOpen] = useState(false);

  const cycleSeconds =
    rally.departedAt && rally.returnedAt
      ? (new Date(rally.returnedAt).getTime() - new Date(rally.departedAt).getTime()) / 1000
      : null;

  const fillRate =
    rally.rallyCapacity && rally.actualTroops !== null && rally.rallyCapacity > 0
      ? rally.actualTroops / rally.rallyCapacity
      : null;

  return (
    <Card
      title={
        <span className="row">
          {rally.wave === null ? t.sessionDetail.rally : t.sessionDetail.waveLabel(rally.wave)}
          {rally.sequence === null ? null : <span className="faint">#{rally.sequence}</span>}
          <Link to={`/players/${encodeURIComponent(rally.leaderPlayerId)}`}>
            <Value absent={t.common.unnamedLead}>{rally.leaderPlayerName}</Value>
          </Link>
        </span>
      }
      actions={
        <button type="button" className="btn btn--sm btn--subtle" onClick={() => setOpen((v) => !v)}>
          {open ? t.sessionDetail.hideJoiners : t.sessionDetail.showJoiners(rally.members.length)}
        </button>
      }
    >
      <div className="row" style={{ gap: 20 }}>
        <Figure label={t.sessionDetail.rallyDamage} value={formatCompact(rally.totalDamage)} />
        <Figure
          label={t.sessionDetail.troops}
          value={
            rally.actualTroops === null
              ? null
              : t.sessionDetail.troopsOf(
                  formatNumber(rally.actualTroops) ?? '',
                  rally.rallyCapacity ? formatNumber(rally.rallyCapacity) : null,
                )
          }
          foot={fillRate === null ? undefined : t.sessionDetail.filled(Math.round(fillRate * 100))}
        />
        <Figure label={t.sessionDetail.returnCycle} value={formatDuration(cycleSeconds)} />
        <Figure
          label={t.sessionDetail.leadHeroes}
          value={rally.leaderHeroIds.length === 0 ? null : rally.leaderHeroIds.join(' · ')}
        />
      </div>

      {open ? <MemberTable members={rally.members} /> : null}
    </Card>
  );
}

function Figure({
  label,
  value,
  foot,
  absentLabel,
}: {
  label: string;
  value: string | null;
  foot?: string;
  absentLabel?: string;
}) {
  const t = useT();
  return (
    <div>
      <div className="stat__label">{label}</div>
      <div style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 500 }}>
        {value ?? <span className="absent">{absentLabel ?? t.common.notRecorded}</span>}
      </div>
      {foot ? <div className="faint small">{foot}</div> : null}
    </div>
  );
}

function MemberTable({ members }: { members: readonly RallyMember[] }) {
  const t = useT();
  const columns = t.sessionDetail.columns;

  if (members.length === 0) {
    return (
      <p className="absent small" style={{ marginBottom: 0, marginTop: 10 }}>
        {t.sessionDetail.noJoinersRecorded}
      </p>
    );
  }

  return (
    <div className="table-wrap" style={{ marginTop: 10 }}>
      <table className="data">
        <thead>
          <tr>
            <th className="num">{columns.order}</th>
            <th>{t.common.player}</th>
            <th className="num">{columns.captainHero}</th>
            <th className="num">{columns.infantry}</th>
            <th className="num">{columns.cavalry}</th>
            <th className="num">{columns.archer}</th>
            <th className="num">{columns.totalTroops}</th>
            <th className="num">{columns.damage}</th>
            <th title={columns.loadoutContextTitle}>{columns.loadoutContext}</th>
          </tr>
        </thead>
        <tbody>
          {[...members]
            .sort((a, b) => (a.joinOrder ?? 999) - (b.joinOrder ?? 999))
            .map((member) => (
              <tr key={member.playerId}>
                <td className="num faint">
                  {member.joinOrder ?? <span className="absent">{t.common.dash}</span>}
                </td>
                <td>
                  <Link to={`/players/${encodeURIComponent(member.playerId)}`}>
                    <Value absent={t.common.unnamedPlayer}>{member.playerName}</Value>
                  </Link>
                </td>
                <td className="num mono">
                  {member.captainHeroId ?? <span className="absent">{t.common.dash}</span>}
                </td>
                <TroopCell value={member.infantry} />
                <TroopCell value={member.cavalry} />
                <TroopCell value={member.archer} />
                <TroopCell value={member.totalTroops} />
                <td className="num">
                  {member.damage === null ? (
                    <span className="absent">{t.common.notRecorded}</span>
                  ) : (
                    formatCompact(member.damage)
                  )}
                </td>
                <td>
                  <ContextBadge
                    quality={member.contextQuality}
                    ageSeconds={member.contextAgeSeconds}
                  />
                </td>
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Blank means the operator could not see the number, not that it was zero.
 *
 * The label is a word in both locales (`not recorded` / `미입력`), never an
 * empty cell and never a digit — a 0 sitting in the next row of the same column
 * has to stay visibly a different thing.
 */
function TroopCell({ value }: { value: number | null }) {
  const t = useT();
  return (
    <td className="num">
      {value === null ? (
        <span className="absent" title={t.sessionDetail.troopNotRecordedTitle}>
          {t.sessionDetail.troopNotRecorded}
        </span>
      ) : (
        formatNumber(value)
      )}
    </td>
  );
}

function ContextBadge({
  quality,
  ageSeconds,
}: {
  quality: RallyMember['contextQuality'];
  ageSeconds: number | null;
}) {
  const t = useT();

  if (quality === null) {
    return (
      <Badge tone="UNKNOWN" title={t.sessionDetail.notAssessedTitle}>
        {t.sessionDetail.notAssessed}
      </Badge>
    );
  }

  const age = ageSeconds === null ? null : formatDuration(Math.abs(ageSeconds));
  const direction =
    ageSeconds === null || ageSeconds === 0
      ? ''
      : ageSeconds < 0
        ? t.sessionDetail.before
        : t.sessionDetail.after;
  // The direction is part of the phrase rather than appended by the sentence:
  // English puts it after the duration, Korean before the noun that follows.
  const agePhrase = age === null ? null : direction ? `${age} ${direction}` : age;
  const explanation = t.sessionDetail.contextExplanation[quality];

  return (
    <Badge
      tone={CONTEXT_TONE[quality]}
      title={agePhrase ? t.sessionDetail.contextAge(explanation, agePhrase) : explanation}
    >
      {t.sessionDetail.contextQuality[quality]}
      {agePhrase ? <span className="faint">{agePhrase}</span> : null}
    </Badge>
  );
}
